// "Tâches" tab of the agent drawer: the agent's tasks (GET /api/tasks?agentId=…) with the state
// chips of the task panel, live updates from `task` events, click → task drawer. The cards reuse
// the task panel's `.task-card` look.
import { h, chip, button, emptyState, spinner, setText, setAttr, taskState } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { relativeTime, formatPercent } from '../../format.js';
import { TASK_FILTERS, ACTIVE_STATES, sortTasks, countByFilter, liveProgress } from '../../store.js';
import { taskTimeRef } from '../taskpanel.js';
import { agentTasks } from './model.js';
import './strings.js';

// « blocked » is only shown while some task is blocked (or when it is the active filter).
const FILTERS = ['all', 'running', 'waiting', 'done', 'scheduled', 'blocked'];
const PAGE = 100;

/**
 * Builds the tab.
 * @param {object} ctx app context
 * @param {object} agent {id, name}
 * @returns {{el: HTMLElement, activate(): void, deactivate(): void, destroy(): void}}
 */
export function createTasksTab(ctx, agent) {
  const tasks = new Map();
  const cards = new Map();
  let filter = 'all';
  let loaded = false;
  let loading = false;
  let unavailable = false;
  let exhausted = false;
  let active = false;
  let destroyed = false;
  let tick = null;
  let seq = 0;
  const offs = [];

  const chipsEl = h('div', { class: 'ad-task-filters', role: 'toolbar', 'aria-label': t('filter.label') });
  const notice = h('p', { class: 'ad-notice', role: 'note', hidden: true }, t('ad.tasks.unavailable'));
  const list = h('ul', { class: 'ad-task-list', 'aria-label': t('ad.tasks.listLabel', { name: agent.name }) });
  const empty = h('div', { class: 'ad-task-empty', hidden: true });
  const more = button(t('ad.tasks.more'), { variant: 'ghost', size: 'sm', class: 'ad-task-more', onClick: () => load({ more: true }) });
  more.hidden = true;
  const loadingEl = h('div', { class: 'ad-tab-loading' }, spinner(t('ad.tasks.loading')), h('span', { class: 'muted' }, t('ad.tasks.loading')));
  const el = h('div', { class: 'ad-tasks' }, chipsEl, notice, loadingEl, list, empty, more);

  function fromStore() {
    for (const tk of ctx.store.get().tasks?.values?.() || []) if (tk?.agentId === agent.id && !tasks.has(tk.id)) tasks.set(tk.id, tk);
  }

  async function load({ more: next = false } = {}) {
    if (loading || destroyed) return;
    loading = true;
    const my = ++seq;
    more.disabled = true;
    let before = null;
    if (next) {
      const oldest = Math.min(...[...tasks.values()].map((x) => Date.parse(x.createdAt) || Number(x.createdAt) || Infinity));
      if (Number.isFinite(oldest)) before = oldest;
    }
    try {
      const res = await ctx.api.get(ctx.api.withQuery('/api/tasks', { agentId: agent.id, limit: PAGE, before }));
      if (destroyed || my !== seq) return;
      const items = Array.isArray(res) ? res : Array.isArray(res?.tasks) ? res.tasks : [];
      const now = Date.now();
      for (const tk of items) if (tk && typeof tk.id === 'string' && tk.agentId === agent.id) tasks.set(tk.id, { ...tk, _at: now });
      exhausted = items.length < PAGE;
      unavailable = false;
    } catch (err) {
      if (destroyed || my !== seq) return;
      if (err?.status === 404) { unavailable = true; exhausted = true; fromStore(); } else ctx.toast?.error?.(err);
    } finally {
      if (my === seq) loading = false;
    }
    loaded = true;
    render();
  }

  function createCard(tk) {
    const pill = h('span', { class: 'tc-pill' });
    const title = h('span', { class: 'tc-title' });
    const time = h('span', { class: 'tc-time' });
    const meta = h('span', { class: 'tc-meta mono-label' });
    const fill = h('span', { class: 'tc-fill' });
    const bar = h('span', { class: 'tc-bar', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100' }, fill);
    const btn = h('button', {
      type: 'button', class: 'task-card ad-task-card', dataset: { id: tk.id },
      onClick: () => ctx.navigate(`#/task/${encodeURIComponent(tk.id)}`),
    }, h('span', { class: 'tc-row' }, pill, title, time), meta, bar);
    const card = { li: h('li', { class: 'tc-item' }, btn), btn, pill, title, time, meta, bar, fill, task: null, p: -1 };
    updateCard(card, tk);
    return card;
  }

  function updateProgress(card, now) {
    const tk = card.task;
    const p = Math.round(liveProgress(tk, now, ctx.store.get().serverOffset || 0));
    setText(card.pill, ACTIVE_STATES.has(tk.state) ? formatPercent(p) : t(`state.${taskState(tk)}`));
    if (p !== card.p) {
      card.p = p;
      card.fill.style.setProperty('transform', `scaleX(${p / 100})`);
      setAttr(card.bar, 'aria-valuenow', String(p));
      setAttr(card.bar, 'aria-label', t('task.progressAria', { p }));
    }
  }

  function updateCard(card, tk, now = Date.now()) {
    card.task = tk;
    card.btn.dataset.state = taskState(tk);
    card.btn.classList.toggle('is-late', !!tk.late);
    const title = tk.title || t('task.untitled');
    setText(card.title, title);
    setText(card.meta, [tk.deptName, tk.floorName].filter(Boolean).join(' · ').toUpperCase());
    setText(card.time, relativeTime(taskTimeRef(tk), now, { short: true }));
    setAttr(card.btn, 'aria-label', t('ad.tasks.open', { title: `${title} — ${t(`state.${taskState(tk)}`)}` }));
    updateProgress(card, now);
  }

  function renderChips(all) {
    const counts = countByFilter(all);
    chipsEl.replaceChildren(...FILTERS.filter((f) => f !== 'blocked' || counts.blocked || f === filter).map((f) => chip(t(`filter.${f}`), {
      active: f === filter, count: f === 'all' ? null : counts[f] || null, tone: f === 'waiting' || f === 'blocked' ? 'amber' : undefined,
      onClick: () => { filter = f; render(); },
    })));
  }

  function render() {
    if (destroyed) return;
    loadingEl.hidden = loaded;
    notice.hidden = !unavailable;
    const all = agentTasks(tasks.values(), agent.id);
    renderChips(all);
    const shown = sortTasks(agentTasks(all, agent.id, TASK_FILTERS[filter] || TASK_FILTERS.all));
    const now = Date.now();
    const keep = new Set(shown.map((x) => x.id));
    for (const [id, card] of cards) if (!keep.has(id)) { card.li.remove(); cards.delete(id); }
    const nodes = shown.map((tk) => {
      let card = cards.get(tk.id);
      if (!card) { card = createCard(tk); cards.set(tk.id, card); } else if (card.task !== tk) updateCard(card, tk, now);
      return card.li;
    });
    list.replaceChildren(...nodes);
    empty.hidden = !loaded || shown.length > 0;
    if (!empty.hidden) {
      empty.replaceChildren(all.length
        ? emptyState({ icon: 'search', title: t('tasks.emptyFilter.title'), text: t('ad.tasks.emptyFilter') })
        : emptyState({ icon: 'list', title: t('ad.tasks.empty.title'), text: t('ad.tasks.empty.text', { name: agent.name }) }));
    }
    more.hidden = !loaded || exhausted || unavailable;
    more.disabled = loading;
    syncTick(shown);
  }

  function syncTick(shown) {
    const running = active && shown.some((x) => ACTIVE_STATES.has(x.state));
    if (running && !tick) {
      tick = setInterval(() => {
        const now = Date.now();
        for (const card of cards.values()) if (ACTIVE_STATES.has(card.task.state)) updateProgress(card, now);
      }, 1000);
    } else if (!running && tick) {
      clearInterval(tick);
      tick = null;
    }
  }

  offs.push(ctx.events.on('task', (s) => {
    if (!s || typeof s.id !== 'string' || destroyed) return;
    if (s.agentId === agent.id) tasks.set(s.id, { ...s, _at: Date.now() });
    else if (tasks.has(s.id)) tasks.delete(s.id); // reassigned to another agent
    else return;
    if (loaded) render();
  }));
  offs.push(ctx.events.on('task.removed', (d) => {
    if (d?.id && tasks.delete(d.id) && loaded) render();
  }));
  offs.push(ctx.events.on('hello', () => { if (loaded) load(); }));

  return {
    el,
    activate() {
      active = true;
      if (!loaded && !loading) load();
      else render();
    },
    deactivate() {
      active = false;
      syncTick([]);
    },
    destroy() {
      destroyed = true;
      clearInterval(tick);
      tick = null;
      for (const off of offs.splice(0)) { try { off?.(); } catch { /* ignore */ } }
    },
  };
}
